"use client";

import { useMemo, useRef, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Checkbox,
  ErrorNotice,
  Panel,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, LABEL } from "@/components/tool-kit/styles";
import { FiveForcesDiagram } from "@/components/tools/business-templates/five-forces-diagram";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  buildFiveForces,
  emptyForces,
  type FiveForcesInput,
  FORCES,
  type Force,
  RATINGS,
  type Rating,
  ratingsOf,
  summarizeForces,
} from "@/lib/tool-kit/business-templates/five-forces";
import { layoutDiagram } from "@/lib/tool-kit/business-templates/five-forces-layout";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";
import { splitLines } from "@/lib/tool-kit/essay-templates/text";

/** English strings. A translated content file overrides these through `ui`. */
export const PORTERS_FIVE_FORCES_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  industryHeading: "Which industry?",
  industryLabel: "Industry — one line of business, not a whole sector",
  industryPlaceholder: "e.g. Online grocery retail in the UK",
  forcesHeading: "Rate each force from 1 to 5, and say why",
  ratingLabel: "Pressure on industry profit",
  ratingNone: "Not rated",
  ratingNames: {
    1: "Very low",
    2: "Low",
    3: "Moderate",
    4: "High",
    5: "Very high",
  },
  notesLabel: "Why this rating? Evidence, examples, numbers",
  notesPlaceholder:
    "One point per line — it shows in the diagram and the write-up.",
  driversSummary: "Checklist — tick what is true of this industry",
  forceNames: {
    entrants: "Threat of new entrants",
    suppliers: "Bargaining power of suppliers",
    buyers: "Bargaining power of buyers",
    substitutes: "Threat of substitutes",
    rivalry: "Rivalry among existing competitors",
  },
  forceHints: {
    entrants:
      "How easily can a new firm enter and compete? Barriers to entry keep this low.",
    suppliers:
      "How much can suppliers, including labor, raise prices or cut quality?",
    buyers:
      "How much can customers push prices down or demand more for the same price?",
    substitutes: "Can customers meet the same need another way?",
    rivalry: "How hard do existing competitors fight over customers?",
  },
  drivers: {
    entrants: [
      "Economies of scale are small, so a newcomer can compete at modest size",
      "Network effects are weak or absent",
      "Customers can switch to a newcomer at little cost",
      "Entry needs little capital",
      "Incumbents have no cost, technology or brand advantage a newcomer cannot copy",
      "Newcomers can reach buyers through existing distribution channels",
      "Licences, regulation and other government policy do not block entry",
      "Incumbents are unlikely to fight back hard",
    ],
    suppliers: [
      "A few suppliers dominate and are more concentrated than the industry they sell to",
      "Buyers have few substitutes for what suppliers provide",
      "Supplied goods are differentiated or hard to replace",
      "Switching suppliers is costly for firms in the industry",
      "The industry is not an important customer to its suppliers",
      "Suppliers could move into the industry themselves",
      "Labor is scarce or organized",
    ],
    buyers: [
      "Buyers are few, or each buys a large share of the industry's output",
      "Products are standardized, so buyers can play sellers off against each other",
      "Switching costs for buyers are low",
      "Buyers could make the product themselves",
      "Buyers are well informed about prices and costs",
      "The product is a large share of the buyer's costs, so they are price-sensitive",
      "The product barely affects the quality of the buyer's own product",
    ],
    substitutes: [
      "A different product meets the same need at a better price-performance trade-off",
      "Switching to the substitute costs buyers little",
      "Buyers see the substitute as much like the industry's product",
      "Close substitutes are many or easy to find",
      "Substitutes are improving fast or getting cheaper",
    ],
    rivalry: [
      "Competitors are numerous or about equal in size and power",
      "Industry growth is slow, so firms gain only by taking share",
      "Exit barriers are high, so weak firms stay",
      "Fixed costs are high, which pushes firms to cut prices to fill capacity",
      "Products are hard to tell apart and switching costs are low",
      "Rivals compete mostly on price",
      "Rivals have very different strategies or goals",
    ],
  },
  resultsHeading: "Overall reading",
  statReading: "Reading",
  statMean: "Average rating",
  statStrongest: "Strongest force",
  statRatedPrompt: "Rate all five forces to see the overall reading.",
  diagramHeading: "Diagram",
  diagramTitle: "Porter's Five Forces: {industry}",
  diagramDefaultTitle: "Porter's Five Forces",
  diagramFooter:
    "Shading shows pressure on industry profit, 1 (very low, green) to 5 (very high, red). Framework: M. E. Porter, Harvard Business Review, 1979 and 2008.",
  diagramLabel: "Porter's five forces diagram",
  diagramDescription:
    "Five boxes: new entrants above, substitutes below, suppliers left and buyers right, each with an arrow to competitive rivalry in the middle. Each box shows a pressure rating from 1 to 5.",
  downloadSvg: "Download diagram (SVG)",
  downloadPng: "Download diagram (PNG)",
  exportFailed: "The PNG could not be created. Download the SVG instead.",
  fileName: "porters-five-forces",
  document: {
    heading: "Your five forces write-up",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part of the write-up has your content.",
  },
  defaultTitle: "Porter's Five Forces Analysis",
  titleWithIndustry: "Porter's Five Forces Analysis: {industry}",
  subtitle:
    "Framework: M. E. Porter, “How Competitive Forces Shape Strategy”, Harvard Business Review, 1979; updated in 2008",
  notRated: "Not rated",
  ratingLine: "{name} ({rating}/5)",
  readingHeading: "Overall Reading",
  readingNote:
    "A teaching convention, not Porter's own formula: an average of 2.0 or less reads as attractive, 3.5 or more as unattractive, and anything between as mixed. Porter argued that the strongest force or forces set an industry's profit potential, so read the forces rated 4 or 5 before the average.",
  readings: {
    incomplete: "Rate all five forces to see a reading",
    attractive: "Attractive — the forces leave room for profit",
    mixed: "Mixed — some forces press on profit, others do not",
    unattractive: "Unattractive — the forces hold profit down",
  },
  fields: {
    industry: "Industry",
    mean: "Average rating",
    strongest: "Strongest force",
    reading: "Reading",
    strong: "Forces rated 4 or 5",
  },
  noneStrong: "None",
  industryPrompt:
    "Name the industry. Porter's analysis applies to one line of business — “online grocery in the UK”, not “retail”.",
  tableHead: {
    force: "Force",
    rating: "Pressure on profit",
    drivers: "Checklist items true",
  },
  driversPresent: "{present} of {total}",
  driversHeading: "What makes this force strong here",
  whyPrompts: {
    entrants:
      "Explain which barriers to entry exist (scale, capital, brand, channels, regulation) and whether any newcomer has recently entered.",
    suppliers:
      "Name the main suppliers or inputs, including labor, and explain how easily the industry could switch or do without them.",
    buyers:
      "Say who the buyers are, how concentrated they are, and how easily they can switch or push prices down.",
    substitutes:
      "Name the substitutes — different products that meet the same need — and compare their price and performance with the industry's.",
    rivalry:
      "Describe the competitors, how they compete (price, quality, service), how fast the industry is growing, and how hard it is to leave.",
  },
  strategyHeading: "Implications for Strategy",
  strategyNote:
    "Porter describes strategy as building defenses against the forces or finding a position where they are weaker. Answer these for the forces you rated 4 or 5.",
  strategySteps: [
    "Which force presses hardest on profit, and what exactly causes it?",
    "Where in the industry — which segment, customer or product — is that force weaker?",
    "What could the organization do to defend against it, or to change the force in its favor?",
    "Which trend could make a force stronger or weaker in the next few years?",
  ],
  listSeparator: ", ",
  example: {
    industry: "Online grocery retail in the UK",
    ratings: {
      entrants: 3,
      suppliers: 3,
      buyers: 4,
      substitutes: 2,
      rivalry: 5,
    },
    notes: {
      entrants:
        "Warehouses and delivery vans need heavy capital\nBut supermarkets and app-based couriers already have customers and stock",
      suppliers:
        "Large brands can refuse to give discounts\nOwn-label products reduce the dependence",
      buyers:
        "Switching between grocery sites takes one click\nShoppers compare delivery fees and basket prices",
      substitutes:
        "Shopping in store still meets the same need, and for many shoppers costs less",
      rivalry:
        "Several large chains compete on price, delivery slots and loyalty schemes\nSlim margins and heavy fixed costs in warehouses",
    },
  },
} as const;

const DRIVER_COUNTS: Record<Force, number> = {
  entrants: PORTERS_FIVE_FORCES_GENERATOR_STRINGS.drivers.entrants.length,
  suppliers: PORTERS_FIVE_FORCES_GENERATOR_STRINGS.drivers.suppliers.length,
  buyers: PORTERS_FIVE_FORCES_GENERATOR_STRINGS.drivers.buyers.length,
  substitutes: PORTERS_FIVE_FORCES_GENERATOR_STRINGS.drivers.substitutes.length,
  rivalry: PORTERS_FIVE_FORCES_GENERATOR_STRINGS.drivers.rivalry.length,
};

const EMPTY: FiveForcesInput = {
  industry: "",
  forces: emptyForces(DRIVER_COUNTS),
};

function ForcePanel({
  force,
  value,
  onChange,
  strings,
}: {
  force: Force;
  value: FiveForcesInput["forces"][Force];
  onChange: (next: FiveForcesInput["forces"][Force]) => void;
  strings: typeof PORTERS_FIVE_FORCES_GENERATOR_STRINGS;
}) {
  const id = `ff-${force}`;
  return (
    <Panel title={strings.forceNames[force]}>
      <p className="text-muted-foreground text-sm">
        {strings.forceHints[force]}
      </p>
      <SelectField
        id={`${id}-rating`}
        label={strings.ratingLabel}
        onChange={(rating) =>
          onChange({ ...value, rating: Number(rating) as Rating })
        }
        options={[
          { value: "0", label: strings.ratingNone },
          ...RATINGS.map((rating) => ({
            value: String(rating),
            label: `${rating} — ${strings.ratingNames[rating]}`,
          })),
        ]}
        value={String(value.rating)}
      />
      <TextAreaField
        id={`${id}-notes`}
        label={strings.notesLabel}
        onChange={(notes) => onChange({ ...value, notes })}
        placeholder={strings.notesPlaceholder}
        prose
        rows={3}
        value={value.notes}
      />
      <details className="space-y-2">
        <summary className={`${LABEL} cursor-pointer`}>
          {strings.driversSummary}
        </summary>
        <div className="mt-3 space-y-2">
          {strings.drivers[force].map((label, index) => (
            <Checkbox
              checked={value.drivers[index] === true}
              key={label}
              label={label}
              onChange={(checked) =>
                onChange({
                  ...value,
                  drivers: strings.drivers[force].map((_, position) =>
                    position === index
                      ? checked
                      : value.drivers[position] === true
                  ),
                })
              }
            />
          ))}
        </div>
      </details>
    </Panel>
  );
}

export default function PortersFiveForcesGeneratorWidget() {
  const strings = useToolUi(PORTERS_FIVE_FORCES_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState<FiveForcesInput>(EMPTY);
  const [exportFailed, setExportFailed] = useState(false);
  const holder = useRef<HTMLDivElement>(null);
  const summary = summarizeForces(ratingsOf(input));
  const doc = useMemo(
    () => buildFiveForces(input, strings, fmt),
    [input, strings, fmt]
  );
  const industry = input.industry.trim();
  const diagramTitle = industry
    ? t(strings.diagramTitle, { industry })
    : strings.diagramDefaultTitle;
  const layout = useMemo(
    () =>
      layoutDiagram({
        names: strings.forceNames,
        ratings: ratingsOf(input),
        ratingLines: Object.fromEntries(
          FORCES.map((force) => {
            const rating = input.forces[force].rating;
            return [
              force,
              rating === 0
                ? strings.notRated
                : t(strings.ratingLine, {
                    name: strings.ratingNames[rating],
                    rating: fmt(rating),
                  }),
            ];
          })
        ) as Record<Force, string>,
        notes: Object.fromEntries(
          FORCES.map((force) => [
            force,
            splitLines(input.forces[force].notes)[0] ?? "",
          ])
        ) as Record<Force, string>,
      }),
    [input, strings, fmt]
  );
  const setForce = (force: Force) => (next: FiveForcesInput["forces"][Force]) =>
    setInput((previous) => ({
      ...previous,
      forces: { ...previous.forces, [force]: next },
    }));
  const baseName = chartFilename(industry, strings.fileName);

  function loadExample() {
    const { example } = strings;
    setInput({
      industry: example.industry,
      forces: Object.fromEntries(
        FORCES.map((force) => [
          force,
          {
            rating: example.ratings[force],
            notes: example.notes[force],
            drivers: EMPTY.forces[force].drivers,
          },
        ])
      ) as FiveForcesInput["forces"],
    });
  }

  function svg() {
    return holder.current?.querySelector("svg") ?? null;
  }

  async function savePng() {
    const element = svg();
    if (!element) {
      return;
    }
    try {
      setExportFailed(false);
      await downloadPng(`${baseName}.png`, element, { scale: 2 });
    } catch {
      setExportFailed(true);
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3 print:hidden">
        <button
          className={BUTTON_SECONDARY}
          onClick={loadExample}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <div className="space-y-6 print:hidden">
        <Panel title={strings.industryHeading}>
          <TextField
            id="ff-industry"
            label={strings.industryLabel}
            onChange={(value) =>
              setInput((previous) => ({ ...previous, industry: value }))
            }
            placeholder={strings.industryPlaceholder}
            value={input.industry}
          />
        </Panel>
        <h2 className="font-semibold text-foreground text-lg">
          {strings.forcesHeading}
        </h2>
        <div className="grid gap-4 lg:grid-cols-2">
          {FORCES.map((force) => (
            <ForcePanel
              force={force}
              key={force}
              onChange={setForce(force)}
              strings={strings}
              value={input.forces[force]}
            />
          ))}
        </div>
      </div>

      <section aria-live="polite" className="space-y-3">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.resultsHeading}
        </h2>
        <StatGrid>
          <StatCard
            label={strings.statReading}
            value={
              summary.reading === "incomplete"
                ? strings.statRatedPrompt
                : strings.readings[summary.reading]
            }
          />
          <StatCard
            label={strings.statMean}
            value={
              summary.mean === null
                ? "—"
                : fmt(summary.mean, {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })
            }
          />
          <StatCard
            label={strings.statStrongest}
            value={
              summary.strongest.length === 0
                ? "—"
                : summary.strongest
                    .map((force) => strings.forceNames[force])
                    .join(strings.listSeparator)
            }
          />
        </StatGrid>
      </section>

      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-lg print:hidden">
          {strings.diagramHeading}
        </h2>
        <div
          className="overflow-hidden rounded-lg border border-border bg-white"
          ref={holder}
        >
          <FiveForcesDiagram
            description={strings.diagramDescription}
            footer={strings.diagramFooter}
            id="ff-diagram"
            label={strings.diagramLabel}
            layout={layout}
            title={diagramTitle}
          />
        </div>
        <div className="flex flex-wrap gap-3 print:hidden">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              const element = svg();
              if (element) {
                downloadSvg(`${baseName}.svg`, element);
              }
            }}
            type="button"
          >
            {strings.downloadSvg}
          </button>
          <button className={BUTTON_SECONDARY} onClick={savePng} type="button">
            {strings.downloadPng}
          </button>
        </div>
        {exportFailed ? (
          <ErrorNotice>{strings.exportFailed}</ErrorNotice>
        ) : null}
      </section>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="ff"
        labels={strings.document}
      />
    </div>
  );
}
